import { apiRequest } from './apiRequest'
import { authService } from './authService'

export interface WalletTransaction {
  _id: string
  transactionId?: string
  tenantId: string
  userId: string
  type: 'deposit' | 'withdraw' | 'bonus' | 'transfer_in' | 'transfer_out' | 'refund' | 'adjustment' | 'fee' | 'bet' | 'win' | 'cancel'
  category?: string
  amount: number
  currency?: string
  status: 'pending' | 'processing' | 'reserved' | 'completed' | 'failed' | 'cancelled' | 'expired' | 'locked'
  details?: {
    description?: string
    reference?: string
    bankAccount?: {
      bankCode?: string
      accountNumber?: string
      accountName?: string
    }
  }
  metadata?: {
    depositMethod?: string
    selectedDepositBankName?: string
    selectedDepositAccountName?: string
    selectedDepositAccountNumber?: string
    notes?: string
    depositRouting?: {
      sourceChannel?: string
      reviewReason?: string
    }
  }
  audit?: {
    createdAt?: string
    updatedAt?: string
  }
  createdAt?: string
  updatedAt?: string
}

interface WalletTransactionListResponse {
  success: boolean
  data?: {
    transactions?: WalletTransaction[]
    pagination?: {
      page: number
      limit: number
      total: number
      pages: number
    }
  }
  error?: string
  message?: string
}

interface WalletTransactionDetailResponse {
  success: boolean
  data?: {
    transaction?: WalletDepositResponse['data'] | WalletTransaction | null
  }
  error?: string
  message?: string
  code?: string
}

interface WalletWithdrawalResponse {
  success: boolean
  data?: WalletTransaction & {
    cooldownSeconds?: number
    retryAfterSeconds?: number
    retryAt?: string
    lastTransactionId?: string | null
    lastWithdrawalStatus?: string | null
    userMessage?: string
  }
  error?: string
  message?: string
  code?: string
}

export interface WalletDepositResponse {
  success: boolean
  data?: WalletTransaction & {
    metadata?: {
      paymentGateway?: {
        provider?: string
        channel?: string
        externalRefId?: string
        providerRefId?: string
        externalUserId?: string
        providerStatus?: string
        confirmStatus?: string
        qrRawData?: string
        qrImage?: string
        qrUrl?: string
        qrcodePayload?: {
          qrcode_text?: string
          link?: string
        }
        bank?: string
        accountNumber?: string
        payerBankCode?: string
        payerBankName?: string
        payerAccountNumber?: string
        payerAccountName?: string
        requestedAmount?: number
        createdAt?: string
        providerExpiresAt?: string
        providerExpiresInSeconds?: number
        reuseWindowMinutes?: number
        reuseExpiresAt?: string
        lastReusedAt?: string
        wasReused?: boolean
        requestedAmountAtReuse?: number
      }
    }
  }
  error?: string
  message?: string
  code?: string
  recoveredExistingDeposit?: boolean
  fallback?: {
    allowed: boolean
    targetChannel: 'bank_transfer'
    amount: number
    reasonCode: string
    userMessage?: string
  }
}

function getDepositFailureMessage(
  payload?: {
    code?: string
    message?: string
    error?: string
    data?: unknown
  },
  fallback = 'เกิดข้อผิดพลาดในการส่งรายการฝากเงิน'
): string {
  const code = String(payload?.code || '').trim().toUpperCase()
  const rawMessage = String(payload?.message || payload?.error || '').trim()
  const lowerMessage = rawMessage.toLowerCase()

  if (
    code === 'DEPOSIT_DUPLICATE_EVIDENCE' ||
    lowerMessage.includes('deposit evidence is already linked') ||
    lowerMessage.includes('duplicate_evidence') ||
    lowerMessage.includes('สลิปซ้ำ') ||
    lowerMessage.includes('duplicate slip')
  ) {
    return 'ระบบพบว่าข้อมูลการโอนนี้ถูกผูกกับรายการฝากเดิมในระบบแล้ว กรุณาตรวจสอบประวัติรายการฝากหรือติดต่อเจ้าหน้าที่'
  }

  if (lowerMessage.includes('duplicate entry')) {
    return 'ระบบพบข้อมูลซ้ำในรายการฝากนี้ กรุณาตรวจสอบประวัติรายการฝากหรือลองใหม่อีกครั้ง'
  }

  if (
    code === 'DEPOSIT_ACCOUNT_SMS_ONLY' ||
    lowerMessage.includes('configured for sms only')
  ) {
    return 'บัญชีรับฝากนี้ตั้งค่าเป็น SMS เท่านั้น จึงไม่สามารถแนบสลิปได้'
  }

  if (
    code === 'DEPOSIT_ACCOUNT_SLIP_REQUIRED' ||
    lowerMessage.includes('requires slip attachment')
  ) {
    return 'บัญชีรับฝากนี้กำหนดให้แนบสลิปก่อนส่งรายการ'
  }

  if (rawMessage) {
    return rawMessage
  }

  return fallback
}

const getCurrentUserIds = () => {
  const user = authService.getCurrentUser()
  return {
    tenantId: user?.tenantId || '',
    userId: (user as { _id?: string; id?: string } | null)?._id || user?.id || '',
  }
}

export const walletTransactionService = {
  async getUserTransactions(params?: {
    type?: WalletTransaction['type']
    status?: string
    limit?: number
    page?: number
  }): Promise<WalletTransactionListResponse> {
    const { tenantId, userId } = getCurrentUserIds()
    if (!tenantId || !userId) {
      return { success: false, message: 'ไม่พบข้อมูลผู้ใช้' }
    }

    const search = new URLSearchParams()
    if (params?.type) search.set('type', params.type)
    if (params?.status) search.set('status', params.status)
    if (params?.limit) search.set('limit', String(params.limit))
    if (params?.page) search.set('page', String(params.page))

    const query = search.toString()
    return apiRequest<WalletTransactionListResponse>(
      `/api/v1/wallet/tenant/${tenantId}/users/${userId}/transactions${query ? `?${query}` : ''}`
    )
  },

  async createWithdrawal(payload: {
    amount: number
    bankAccount: {
      bankCode: string
      accountNumber: string
      accountName: string
    }
    reason?: string
  }): Promise<WalletWithdrawalResponse> {
    const { tenantId, userId } = getCurrentUserIds()
    if (!tenantId || !userId) {
      return { success: false, message: 'ไม่พบข้อมูลผู้ใช้' }
    }

    try {
      return await apiRequest<WalletWithdrawalResponse>(
        `/api/v1/wallet/tenant/${tenantId}/users/${userId}/transactions/withdraw`,
        {
          method: 'POST',
          body: payload,
        }
      )
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.message || error?.response?.error || error?.message || 'เกิดข้อผิดพลาดในการส่งคำขอถอนเงิน',
        error: error?.response?.error,
        code: error?.response?.code,
        data: error?.response?.data,
      }
    }
  },

  async createDeposit(payload: {
    amount: number
    paymentMethod: 'bank_transfer' | 'sandspay' | 'payment_gateway'
    reference?: string
    slipData?: {
      imageUrl?: string
      bankCode?: string
      accountNumber?: string
      qrData?: string
    }
    metadata?: Record<string, unknown>
  }): Promise<WalletDepositResponse> {
    const { tenantId, userId } = getCurrentUserIds()
    if (!tenantId || !userId) {
      return { success: false, message: 'ไม่พบข้อมูลผู้ใช้' }
    }

    try {
      return await apiRequest<WalletDepositResponse>(
        `/api/v1/wallet/tenant/${tenantId}/users/${userId}/transactions/deposit`,
        {
          method: 'POST',
          body: payload,
        }
      )
    } catch (error: any) {
      const responsePayload = {
        message: error?.response?.message || error?.response?.error || error?.message,
        error: error?.response?.error,
        code: error?.response?.code,
        data: error?.response?.data,
      }
      return {
        success: false,
        message: getDepositFailureMessage(responsePayload),
        error: error?.response?.error,
        code: error?.response?.code,
        data: error?.response?.data,
        fallback: error?.response?.fallback,
      }
    }
  },

  async getTransactionDetails(transactionId: string): Promise<WalletTransactionDetailResponse> {
    const { tenantId } = getCurrentUserIds()
    if (!tenantId || !transactionId) {
      return { success: false, message: 'ไม่พบข้อมูลผู้ใช้หรือรายการฝาก' }
    }

    try {
      return await apiRequest<WalletTransactionDetailResponse>(
        `/api/v1/wallet/tenant/${tenantId}/transactions/${transactionId}`
      )
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.message || error?.response?.error || error?.message || 'เกิดข้อผิดพลาดในการดึงข้อมูลรายการฝาก',
        error: error?.response?.error,
        code: error?.response?.code,
      }
    }
  },

  async attachSlipToDepositTransaction(
    transactionId: string,
    payload: {
      slipData: {
        imageUrl?: string
        bankCode?: string
        accountNumber?: string
        qrData?: string
      }
    }
  ): Promise<WalletDepositResponse> {
    const { tenantId, userId } = getCurrentUserIds()
    if (!tenantId || !userId || !transactionId) {
      return { success: false, message: 'ไม่พบข้อมูลผู้ใช้หรือรายการฝาก' }
    }

    try {
      return await apiRequest<WalletDepositResponse>(
        `/api/v1/wallet/tenant/${tenantId}/users/${userId}/transactions/${transactionId}/attach-slip`,
        {
          method: 'POST',
          body: payload,
        }
      )
    } catch (error: any) {
      const responsePayload = {
        message: error?.response?.message || error?.response?.error || error?.message,
        error: error?.response?.error,
        code: error?.response?.code,
        data: error?.response?.data,
      }
      return {
        success: false,
        message: getDepositFailureMessage(responsePayload, 'เกิดข้อผิดพลาดในการแนบสลิปภายหลัง'),
        error: error?.response?.error,
        code: error?.response?.code,
        data: error?.response?.data,
      }
    }
  },
}
